import { Box, Flex, Heading, Icon, Text, VStack, useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import CustomButton from 'components/CustomButton';
import InputNumber from 'components/InputNumber';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FiCopy } from 'react-icons/fi';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { QRCode } from 'react-qrcode-logo';
import { useCopyToClipboard } from 'react-use';
import { generateInstantCharge, getCharge } from 'services/api.service';
import { InstantQrCode } from 'types/Charge';
import { maskCurrency } from 'utils/number';
import { getVariable } from 'whitelabel';

const DepositPage: React.FC = () => {
  const toast = useToast();
  const { userData } = useAuth();
  const [, copyToClipboard] = useCopyToClipboard();
  const { control, handleSubmit } = useForm<{ amount: number }>();

  const depositEnabled = (userData?.features as { deposit?: boolean } | undefined)?.deposit;

  const [loading, setLoading] = React.useState(false);
  const [charge, setCharge] = React.useState<InstantQrCode | null>(null);

  // Enquanto o QR está na tela, faz polling do status a cada segundo.
  const { data: polled } = useQuery(['deposit-charge', charge?.id], () => getCharge(charge!.id), {
    enabled: !!charge,
    refetchInterval: (data) => {
      const status = (data as { status?: string } | undefined)?.status;
      if (status && status !== 'PENDING') return false;
      return 1000;
    },
  });

  const status = (polled as { status?: string } | undefined)?.status ?? 'PENDING';
  const isPaid = status === 'PAID' || status === 'FINISHED';

  const onSubmit = async (form: { amount: number }) => {
    if (!form.amount || form.amount <= 0) return;
    setLoading(true);
    try {
      const created = await generateInstantCharge({ amount: form.amount, deposit: true });
      setCharge(created);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao gerar QR Code de depósito.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => setCharge(null);

  const copyCode = () => {
    if (!charge) return;
    copyToClipboard(charge.qrCode);
    toast({
      title: 'Código copiado',
      position: 'top',
      status: 'success',
      duration: 1500,
      isClosable: true,
    });
  };

  if (!depositEnabled) {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '80px' }} align="center">
        <Box bg="white" borderRadius="20px" p={8} w={{ base: '100%', md: '480px' }} boxShadow="sm">
          <VStack spacing={4} align="stretch">
            <Heading size="lg">Depositar</Heading>
            <Text color="gray.500">
              Depósito não habilitado para esta conta. Fale com o suporte para liberar.
            </Text>
          </VStack>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex direction="column" pt={{ base: '120px', md: '80px' }} align="center">
      <Box bg="white" borderRadius="20px" p={8} w={{ base: '100%', md: '480px' }} boxShadow="sm">
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Depositar</Heading>

          {!charge && (
            <>
              <Text color="gray.500">
                Gere um QR Code Pix para depositar na sua conta. O valor é creditado no seu
                saldo, sem gerar venda.
              </Text>
              <InputNumber
                control={control}
                name="amount"
                label="Valor do depósito"
                placeholder="R$ 0,00"
                rules={{
                  required: 'Informe o valor',
                  validate: {
                    positive: (v: number) => v > 0 || 'O valor deve ser maior que 0',
                  },
                }}
              />
              <CustomButton colorScheme="green" isLoading={loading} onClick={handleSubmit(onSubmit)}>
                Gerar QR Code
              </CustomButton>
            </>
          )}

          {charge && !isPaid && (
            <VStack spacing={5} align="center">
              <Text>
                <strong>Valor:</strong> {maskCurrency(Number(charge.amount))}
              </Text>
              {charge.fee != null && charge.netAmount != null && (
                <Text color="gray.500" fontSize="sm">
                  Taxa {maskCurrency(charge.fee)} · Você receberá {maskCurrency(charge.netAmount)}
                </Text>
              )}
              <QRCode
                value={charge.qrCode}
                logoImage={getVariable('logo')}
                size={256}
                removeQrCodeBehindLogo
              />
              <CustomButton leftIcon={<FiCopy />} onClick={copyCode}>
                Copiar código Pix
              </CustomButton>
              <Text color="gray.400" fontSize="sm">
                Aguardando pagamento...
              </Text>
              <CustomButton variant="ghost" onClick={reset}>
                Novo depósito
              </CustomButton>
            </VStack>
          )}

          {charge && isPaid && (
            <VStack spacing={5} align="center">
              <Icon as={IoCheckmarkCircle} boxSize={20} color="green.400" />
              <Heading size="md">Depósito recebido</Heading>
              <Text>
                <strong>Valor:</strong> {maskCurrency(Number(charge.amount))}
              </Text>
              {charge.netAmount != null && (
                <Text color="gray.500" fontSize="sm">
                  Creditado {maskCurrency(charge.netAmount)}
                  {charge.fee ? ` (taxa ${maskCurrency(charge.fee)})` : ''}
                </Text>
              )}
              <CustomButton colorScheme="green" onClick={reset}>
                Novo depósito
              </CustomButton>
            </VStack>
          )}
        </VStack>
      </Box>
    </Flex>
  );
};

export default DepositPage;
